import { useCallback, useEffect, useState } from "react";
import { Download, BarChart3, Filter } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { endpoints } from "@/lib/api";
import { PageHeader, Card, Stat, Btn, Loading, fmtDateTime } from "./AdminUI";

const SYM = { RWF: "RWF ", USD: "$", EUR: "€" };
const Input = (p) => <input {...p} className="h-10 rounded-lg bg-[#0A0C10] border border-[#273042] px-3 text-white text-sm outline-none focus:border-[#E11D2A]/60" />;
const Sel = ({ children, ...p }) => <select {...p} className="h-10 rounded-lg bg-[#0A0C10] border border-[#273042] px-3 text-white text-sm">{children}</select>;

export default function AdminReports() {
  const [tab, setTab] = useState("payments");
  const [filters, setFilters] = useState({ date_from: "", date_to: "", currency: "", method: "", plan: "", status: "" });
  const [report, setReport] = useState({ items: [], totalsByCurrency: {}, count: 0, paidCount: 0 });
  const [revenue, setRevenue] = useState([]);
  const [chartCur, setChartCur] = useState("RWF");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const [r, rev] = await Promise.all([endpoints.adminPaymentsReport(params), endpoints.adminRevenue()]);
      setReport(r.data); setRevenue(rev.data);
    } catch { /* noop */ } finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const totals = report.totalsByCurrency || {};
  const currencies = Object.keys(totals);

  const exportCsv = () => {
    const header = "Customer,Plan,Method,Amount,Currency,Status,Date\n";
    const rows = report.items.map((p) => `${p.userPhone || ""},${p.planLabel || p.plan || ""},${p.method || ""},${p.amount || 0},${p.currency || ""},${p.status || ""},${p.paidAt || p.createdAt || ""}`).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "bbkigali-payments.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <PageHeader title="Reports" desc="Financial and ticket reporting. Revenue kept in original currency — EUR, USD and RWF are never merged.">
        {tab === "payments" && <Btn onClick={exportCsv} variant="ghost" data-testid="reports-export-btn"><Download className="w-4 h-4" /> Export CSV</Btn>}
      </PageHeader>

      <div className="inline-flex rounded-xl bg-[#121620] border border-[#273042] p-1 mb-6">
        {[["payments", "Payments & Subscriptions"], ["tickets", "Ticket Sales"], ["optouts", "Notification Opt-outs"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`report-tab-${k}`}
            className={`px-5 h-9 rounded-lg text-sm font-semibold transition-colors ${tab === k ? "bg-[#E11D2A] text-white" : "text-slate-300 hover:text-white"}`}>{l}</button>
        ))}
      </div>

      {tab === "tickets" ? <TicketReport /> : tab === "optouts" ? <OptOutReport /> : (
      <>
      <Card className="p-4 mb-6">
        <div className="flex items-center gap-2 text-slate-300 text-xs font-semibold uppercase tracking-wider mb-3"><Filter className="w-4 h-4" /> Filters</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2 items-center">
          <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="filter-from" />
          <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="filter-to" />
          <Sel value={filters.currency} onChange={(e) => setFilters({ ...filters, currency: e.target.value })} data-testid="filter-currency">
            <option value="">All currencies</option><option>RWF</option><option>USD</option><option>EUR</option>
          </Sel>
          <Sel value={filters.method} onChange={(e) => setFilters({ ...filters, method: e.target.value })} data-testid="filter-method">
            <option value="">All methods</option><option value="mtn_momo">MTN MoMo</option><option value="stripe">Card (Stripe)</option><option value="paypal">PayPal</option><option value="dev">Dev</option>
          </Sel>
          <Sel value={filters.plan} onChange={(e) => setFilters({ ...filters, plan: e.target.value })} data-testid="filter-plan">
            <option value="">All packages</option><option value="basic_monthly">Basic Monthly</option><option value="basic_yearly">Basic Yearly</option><option value="premium_monthly">Premium Monthly</option><option value="premium_yearly">Premium Yearly</option>
          </Sel>
          <Sel value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="filter-status">
            <option value="">All statuses</option><option value="paid">Paid</option><option value="pending">Pending</option><option value="failed">Failed</option>
          </Sel>
          <Btn onClick={() => setFilters({ date_from: "", date_to: "", currency: "", method: "", plan: "", status: "" })} variant="ghost" data-testid="filter-reset">Reset</Btn>
        </div>
      </Card>

      {loading ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {["RWF", "USD", "EUR"].map((c) => (
              <Stat key={c} label={`Collected · ${c}`} value={`${SYM[c]}${(totals[c] || 0).toLocaleString()}`} icon={BarChart3} color={c === "RWF" ? "#22C55E" : c === "USD" ? "#2563EB" : "#E11D2A"} />
            ))}
            <Stat label="Paid orders" value={report.paidCount} color="#2563EB" />
          </div>

          <Card className="p-5 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold">Revenue by day</h3>
              <Sel value={chartCur} onChange={(e) => setChartCur(e.target.value)} data-testid="chart-currency">
                <option>RWF</option><option>USD</option><option>EUR</option>
              </Sel>
            </div>
            {revenue.filter((r) => r.currency === chartCur).length === 0 ? <div className="text-slate-500 text-sm py-16 text-center">No {chartCur} data yet.</div> : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={revenue.filter((r) => r.currency === chartCur)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#273042" />
                  <XAxis dataKey="period" stroke="#64748B" fontSize={11} />
                  <YAxis stroke="#64748B" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#121620", border: "1px solid #273042", borderRadius: 8, color: "#fff" }} cursor={{ fill: "rgba(225,29,42,0.08)" }} />
                  <Bar dataKey="amount" fill="#E11D2A" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          <Card className="overflow-hidden">
            <div className="p-4 border-b border-[#273042] text-white font-bold text-sm">{report.count} transaction(s)</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-slate-400 text-xs uppercase tracking-wider">
                  {["Customer", "Package", "Method", "Amount", "Currency", "Status", "Date"].map((h) => <th key={h} className="text-left font-semibold px-4 py-2.5">{h}</th>)}
                </tr></thead>
                <tbody>
                  {report.items.map((p) => (
                    <tr key={p.id} className="border-t border-[#273042]" data-testid={`report-row-${p.id}`}>
                      <td className="px-4 py-2.5 text-slate-300">{p.userPhone || p.userEmail || "—"}</td>
                      <td className="px-4 py-2.5 text-slate-300">{p.planLabel || p.plan}</td>
                      <td className="px-4 py-2.5 text-slate-400">{p.method}</td>
                      <td className="px-4 py-2.5 text-white font-semibold">{(p.amount || 0).toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-slate-400">{p.currency}</td>
                      <td className="px-4 py-2.5"><span className={p.status === "paid" ? "text-emerald-400" : p.status === "failed" ? "text-red-400" : "text-amber-400"}>{p.status}</span></td>
                      <td className="px-4 py-2.5 text-slate-500 text-xs whitespace-nowrap">{fmtDateTime(p.paidAt || p.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
      </>
      )}
    </div>
  );
}

function TicketReport() {
  const [filters, setFilters] = useState({ event_id: "", status: "", method: "", date_from: "", date_to: "" });
  const [events, setEvents] = useState([]);
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { endpoints.adminEvents().then((r) => setEvents(r.data.events || [])).catch(() => {}); }, []);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await endpoints.adminTicketsReport(params);
      setRep(data);
    } catch { /* noop */ } finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    if (!rep) return;
    const header = "Ticket,Event,Category,Holder,Phone,Price,Currency,Status,Payment,ScannedBy,CancelledBy,Reason,Date\n";
    const rows = (rep.items || []).map((t) => [t.ticketNumber, t.event, t.category, t.holder, t.buyerPhone, t.price, t.currency, t.status, t.paymentStatus, t.scannedByName || "", t.cancelledByName || "", (t.cancelReason || "").replace(/,/g, " "), (t.createdAt || "")].join(",")).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "bbkigali-tickets.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const s = rep?.summary || {};
  const gross = s.grossByCurrency || {};
  const refunds = s.refundsByCurrency || {};

  return (
    <>
      <Card className="p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-slate-300 text-xs font-semibold uppercase tracking-wider"><Filter className="w-4 h-4" /> Filters</div>
          <Btn onClick={exportCsv} variant="ghost" data-testid="tickets-export-btn"><Download className="w-4 h-4" /> Export CSV</Btn>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 items-center">
          <Sel value={filters.event_id} onChange={(e) => setFilters({ ...filters, event_id: e.target.value })} data-testid="tfilter-event">
            <option value="">All events</option>{events.map((ev) => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
          </Sel>
          <Sel value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="tfilter-status">
            <option value="">All statuses</option><option value="issued">Valid / unused</option><option value="used">Used / scanned</option><option value="cancelled">Cancelled</option>
          </Sel>
          <Sel value={filters.method} onChange={(e) => setFilters({ ...filters, method: e.target.value })} data-testid="tfilter-method">
            <option value="">All methods</option><option value="mtn_momo">MTN MoMo</option><option value="stripe">Card (Stripe)</option><option value="paypal">PayPal</option>
          </Sel>
          <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="tfilter-from" />
          <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="tfilter-to" />
          <Btn onClick={() => setFilters({ event_id: "", status: "", method: "", date_from: "", date_to: "" })} variant="ghost" data-testid="tfilter-reset">Reset</Btn>
        </div>
      </Card>

      {loading ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4" data-testid="ticket-report-summary">
            <Stat label="Total tickets" value={s.total || 0} icon={BarChart3} color="#2563EB" />
            <Stat label="Paid" value={s.paid || 0} color="#22C55E" />
            <Stat label="Complimentary" value={s.complimentary || 0} color="#93B4FF" />
            <Stat label="Pending payments" value={s.pendingPayments || 0} color="#F59E0B" />
            <Stat label="Used / scanned" value={s.used || 0} color="#94A3B8" />
            <Stat label="Unused (valid)" value={s.unused || 0} color="#22C55E" />
            <Stat label="Cancelled" value={s.cancelled || 0} color="#E11D2A" />
            <Stat label="Gross (RWF)" value={`RWF ${(gross.RWF || 0).toLocaleString()}`} color="#22C55E" />
          </div>
          {(Object.keys(gross).length > 1 || refunds.RWF) && (
            <Card className="p-4 mb-6 text-sm text-slate-300">
              <span className="font-semibold text-white">Gross collections:</span> {Object.entries(gross).map(([c, v]) => `${c} ${v.toLocaleString()}`).join(" · ") || "—"}
              {Object.keys(refunds).length > 0 && <span className="ml-4"><span className="font-semibold text-red-400">Refunds/cancelled value:</span> {Object.entries(refunds).map(([c, v]) => `${c} ${v.toLocaleString()}`).join(" · ")}</span>}
            </Card>
          )}

          <Card className="overflow-hidden mb-6">
            <div className="p-4 border-b border-[#273042] text-white font-bold text-sm">Sales by event & category</div>
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead><tr className="text-slate-400 text-xs uppercase">{["Event", "Category", "Sold", "Used", "Cancelled", "Gross"].map((h) => <th key={h} className="text-left px-4 py-2.5 font-semibold">{h}</th>)}</tr></thead>
              <tbody>{(rep?.byCategory || []).map((c, i) => (
                <tr key={i} className="border-t border-[#273042]" data-testid={`tcat-row-${i}`}>
                  <td className="px-4 py-2.5 text-white">{c.event}</td><td className="px-4 py-2.5 text-slate-300">{c.category}</td>
                  <td className="px-4 py-2.5 text-[#F5B301]">{c.sold}</td><td className="px-4 py-2.5 text-slate-400">{c.used}</td>
                  <td className="px-4 py-2.5 text-red-400">{c.cancelled}</td><td className="px-4 py-2.5 text-emerald-400">{c.gross.toLocaleString()} {c.currency}</td>
                </tr>))}</tbody>
            </table></div>
          </Card>

          <Card className="overflow-hidden">
            <div className="p-4 border-b border-[#273042] text-white font-bold text-sm">{(rep?.items || []).length} ticket(s)</div>
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead><tr className="text-slate-400 text-xs uppercase">{["Ticket", "Event", "Category", "Holder", "Price", "Status", "Scanned/Cancelled by", "Date"].map((h) => <th key={h} className="text-left px-4 py-2.5 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>{(rep?.items || []).map((t, i) => (
                <tr key={i} className="border-t border-[#273042]" data-testid={`treport-row-${i}`}>
                  <td className="px-4 py-2.5 text-[#F5B301] font-bold whitespace-nowrap">{t.ticketNumber}</td>
                  <td className="px-4 py-2.5 text-slate-300">{t.event}</td><td className="px-4 py-2.5 text-slate-400">{t.category}</td>
                  <td className="px-4 py-2.5 text-white">{t.holder}</td>
                  <td className="px-4 py-2.5 text-slate-300">{t.paymentStatus === "complimentary" ? "—" : `${(t.price || 0).toLocaleString()} ${t.currency}`}</td>
                  <td className="px-4 py-2.5"><span className={t.status === "used" ? "text-slate-400" : t.status === "cancelled" ? "text-red-400" : "text-emerald-400"}>{t.status}</span></td>
                  <td className="px-4 py-2.5 text-slate-400 text-xs">{t.scannedByName || t.cancelledByName || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs whitespace-nowrap">{fmtDateTime(t.createdAt)}</td>
                </tr>))}</tbody>
            </table></div>
          </Card>
        </>
      )}
    </>
  );
}

function OptOutReport() {
  const [q, setQ] = useState("");
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await endpoints.adminOptOuts(q); setData(r.data); }
    catch { /* noop */ } finally { setLoading(false); }
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const exportCsv = () => {
    const header = "CustomerID,Name,Phone,Email,Status,OptedOutAt,Channel,ReactivatedAt,BlockedCategories\n";
    const rows = (data.items || []).map((u) => [u.id, u.name, u.phone, u.email, u.status, u.optOutAt || "", u.optOutChannel || "", u.reactivatedAt || "", (u.blockedCategories || []).join("|")].map((v) => `"${String(v).replace(/"/g, "'")}"`).join(",")).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "bbkigali-notification-optouts.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="optout-report">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <Stat label="Opted out (non-essential blocked)" value={data.total || 0} color="#E11D2A" />
      </div>
      <Card className="p-4 mb-5 flex flex-col sm:flex-row gap-3 sm:items-center">
        <Input placeholder="Search name, phone or email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="optout-search" className="flex-1 h-10 rounded-lg bg-[#0A0C10] border border-[#273042] px-3 text-white text-sm outline-none focus:border-[#E11D2A]/60 w-full" />
        <Btn onClick={exportCsv} variant="ghost" data-testid="optout-export"><Download className="w-4 h-4" /> Export CSV</Btn>
      </Card>
      {loading ? <Loading /> : (
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-[#273042] text-white font-bold text-sm">{(data.items || []).length} customer(s)</div>
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="text-slate-400 text-xs uppercase">{["Customer ID", "Name", "Phone", "Email", "Opted out", "Channel", "Reactivated", "Status"].map((h) => <th key={h} className="text-left px-4 py-2.5 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody>{(data.items || []).map((u) => (
              <tr key={u.id} className="border-t border-[#273042]" data-testid={`optout-row-${u.id}`}>
                <td className="px-4 py-2.5 text-slate-500 text-xs font-mono">{u.id.slice(-8)}</td>
                <td className="px-4 py-2.5 text-white">{u.name}</td>
                <td className="px-4 py-2.5 text-slate-300 whitespace-nowrap">{u.phone}</td>
                <td className="px-4 py-2.5 text-slate-300">{u.email}</td>
                <td className="px-4 py-2.5 text-slate-500 text-xs whitespace-nowrap">{fmtDateTime(u.optOutAt)}</td>
                <td className="px-4 py-2.5 text-slate-400 text-xs capitalize">{u.optOutChannel}</td>
                <td className="px-4 py-2.5 text-slate-500 text-xs whitespace-nowrap">{u.reactivatedAt ? fmtDateTime(u.reactivatedAt) : "—"}</td>
                <td className="px-4 py-2.5"><span className={u.status === "opted_out" ? "text-red-400 font-semibold" : "text-emerald-400"}>{u.status === "opted_out" ? "Blocked" : "Active"}</span></td>
              </tr>))}
              {(data.items || []).length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500">No opt-outs recorded.</td></tr>}
            </tbody>
          </table></div>
        </Card>
      )}
    </div>
  );
}

